/**
 * Centro de Breeding — UI (revamp: layout de análise + modos Feromônios/Grátis).
 * Consome src/breeding/breeding-core.mjs.
 */
import { t, formatNumber, onLocaleChange, rarityLabel, typeLabel } from '../i18n/index.js';
import { idlecoinMark, uiIcon } from '../ui-icons.js';
import { specimenTierForMult } from '../capture-balance.mjs';
import {
  breedingCompatibility as coreCompat,
  breedingCost as coreCost,
  rollBreedingDelta as coreDelta,
  breedChild as coreBreed,
  incubationDefeatsFor as coreIncubation,
  pheromoneCostFor as corePheromoneCost,
  projectedQualityRange as coreQualityRange,
  deltaTableForMode as coreDeltaTable,
  ivInheritanceChance as coreInheritChance,
  normalizeBreedingMode as coreMode,
} from './breeding-core.mjs';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const QUALITY_FLOOR = 1;
const QUALITY_CAP = 4.5;

export function breedingCompatibility(a, b) {
  if (!a || !b) return { ok: false, percent: 0, reason: 'empty' };
  if (String(a.id) === String(b.id)) return { ok: false, percent: 0, reason: 'same' };
  const value = Number(coreCompat(a, b)) || 0;
  if (value <= 0) return { ok: false, percent: 0, reason: 'species' };
  return { ok: true, percent: Math.round(value * 100) };
}

export function breedingCost(child, opts = {}) {
  return coreCost(child, {
    mode: opts.mode,
    doubleStones: !!opts.doubleStones,
    discount: opts.cheaper || opts.discount || 0,
  });
}

export function rollBreedingDelta(opts = {}) {
  const delta = coreDelta({ mode: opts.mode, pheromones: opts.pheromones });
  return { delta, label: `+${Number(delta).toFixed(2)}` };
}

export function breedChild(a, b, opts = {}) {
  return coreBreed(a, b, { mode: opts.mode, pheromones: opts.pheromones, doubleStones: !!opts.doubleStones });
}

export function incubationDefeatsFor(egg) {
  return coreIncubation(egg);
}

export const pheromoneCostFor = corePheromoneCost;
export const normalizeBreedingMode = coreMode;

let root = null;
let hooks = {
  getState: () => ({}),
  getBoxPokemon: () => [],
  pokemonImage: () => '',
  title: (s) => s,
  rarityColor: () => '#a9b5ae',
  ivScore: () => 0,
  getCoins: () => 0,
  getItemCount: () => 0,
  itemImg: () => '',
  itemName: (s) => s,
  getSpeciesTypes: () => [],
  typeColor: () => '#777',
  stoneRows: () => [],
  onCreateEgg: () => ({ ok: false }),
  onSelectParent: () => {},
  onClearParent: () => {},
  onSetMode: () => {},
  onToggleDoubleStones: () => {},
  showToast: () => {},
  save: () => {},
};
const ui = { open: false, query: '', sort: 'iv', pickingSlot: null };

function breedingState() {
  const raw = hooks.getState().breeding || {};
  return {
    pair: raw.pair || [null, null],
    incubator: raw.incubator || [null, null],
    mode: coreMode(raw.mode ?? (raw.bonuses?.cheaper ? 'free' : 'pheromones')),
    doubleStones: !!raw.doubleStones,
  };
}

function pairPokemon() {
  const box = hooks.getBoxPokemon();
  const all = [...box, ...(hooks.getState().collection || [])];
  return breedingState().pair.map((id) => (id == null ? null : all.find((p) => String(p.id) === String(id)) || null));
}

const fmtDelta = (value) => `+${Number(value) >= 0.1 ? Number(value).toFixed(2) : Number(value)}`;
const pct = (value) => Math.round(value * 100);
const scalePos = (q) => Math.max(0, Math.min(100, ((q - QUALITY_FLOOR) / (QUALITY_CAP - QUALITY_FLOOR)) * 100));

/* ---- cabeçalho ----------------------------------------------------------- */
function headerHtml() {
  const coins = hooks.getCoins();
  const pheromones = hooks.getItemCount('pheromone');
  const pheromoneImg = hooks.itemImg('pheromone');
  return `<header class="breeding-head">
    <div class="breeding-head-id">
      <span class="breeding-head-icon">${uiIcon('egg')}</span>
      <div class="breeding-head-copy">
        <span class="eyebrow">${esc(t('breeding.eyebrow'))}</span>
        <h2 id="breedingTitle">${esc(t('breeding.title'))}</h2>
        <p>${esc(t('breeding.intro'))}</p>
      </div>
    </div>
    <div class="breeding-wallet">
      <span class="breeding-wallet-chip" title="IdleCoin">${idlecoinMark(15)}<b>${formatNumber(coins)}</b></span>
      <span class="breeding-wallet-chip" title="${esc(hooks.itemName('pheromone'))}">${pheromoneImg ? `<img src="${esc(pheromoneImg)}" alt="">` : ''}<b>${formatNumber(pheromones)}</b></span>
      <button type="button" class="dialog-close breeding-close" data-breeding-action="close" aria-label="${esc(t('breeding.close'))}">&times;</button>
    </div>
  </header>`;
}

/* ---- par reprodutor ------------------------------------------------------ */
function parentSlotHtml(p, index) {
  const tag = index === 0 ? '♂' : '♀';
  const picking = ui.pickingSlot === index;
  const label = `${tag} ${esc(t('breeding.slot', { n: index + 1 }))}`;
  if (!p) {
    return `<button type="button" class="breeding-parent empty${picking ? ' picking' : ''}" data-breeding-pick="${index}">
      <span class="breeding-parent-tag">${label}</span>
      <span class="breeding-parent-q">?</span>
      <small>${esc(t('breeding.pickFromBox'))}</small>
    </button>`;
  }
  return `<div class="breeding-parent filled${picking ? ' picking' : ''}" style="--rarity:${esc(hooks.rarityColor(p.rarity))}" data-breeding-pick="${index}" role="button" tabindex="0">
    <span class="breeding-parent-tag">${label}</span>
    <button type="button" class="breeding-parent-clear" data-breeding-clear="${index}" aria-label="${esc(t('breeding.clearSlot'))}">&times;</button>
    <span class="breeding-parent-sprite"><img src="${esc(hooks.pokemonImage(p.name))}" alt=""></span>
    <b>${esc(hooks.title(p.name))}</b>
    <small>${esc(t('common.lv'))} ${p.level}</small>
    <span class="breeding-parent-chips"><em class="chip-iv">IV ${esc(hooks.ivScore(p))}</em><em class="chip-q">Q ${Number(p.mult || 1).toFixed(2)}</em></span>
    <span class="breeding-parent-rarity">${esc(rarityLabel(p.rarity || 'Comum')).toUpperCase()}</span>
  </div>`;
}

function pairColHtml(a, b) {
  return `<section class="breeding-panel breeding-pair-col">
    <h3>${esc(t('breeding.pair'))}</h3>
    <div class="breeding-pair-slots">
      ${parentSlotHtml(a, 0)}
      <span class="breeding-pair-link" aria-hidden="true">${uiIcon('dna')}</span>
      ${parentSlotHtml(b, 1)}
    </div>
    <p class="breeding-warn">${esc(t('breeding.parentsConsumed'))}</p>
  </section>`;
}

/* ---- análise -------------------------------------------------------------- */
function analysisStepsHtml() {
  const steps = [
    ['breeding.step1Title', 'breeding.step1Text'],
    ['breeding.step2Title', 'breeding.step2Text'],
    ['breeding.step3Title', 'breeding.step3Text'],
  ];
  return `<div class="breeding-steps">${steps.map(([titleKey, textKey], i) => `
    <div class="breeding-step"><b>${i + 1}</b><span><strong>${esc(t(titleKey))}</strong><small>${esc(t(textKey))}</small></span></div>`).join('')}
  </div>`;
}

function analysisEmptyHtml() {
  return `<div class="breeding-analysis-empty">
    <div class="breeding-child idle">
      <span class="breeding-child-sprite">?</span>
      <p>${esc(t('breeding.emptyAnalysis'))}</p>
    </div>
    ${analysisStepsHtml()}
    <button type="button" class="breeding-cta" disabled>${esc(t('breeding.needPair'))}</button>
  </div>`;
}

function analysisIncompatibleHtml() {
  return `<div class="breeding-analysis-empty">
    <div class="breeding-child idle bad">
      <span class="breeding-child-sprite">×</span>
      <p><b>${esc(t('breeding.incompatible'))}</b> — ${esc(t('breeding.incompatibleHint'))}</p>
    </div>
    <button type="button" class="breeding-cta" disabled>${esc(t('breeding.incompatible'))}</button>
  </div>`;
}

function typeBadgesHtml(types) {
  return (types || []).map((type) => `<span class="breeding-type-badge" style="--type:${esc(hooks.typeColor(type))}">${esc(typeLabel(type, { upper: true }))}</span>`).join('');
}

function analysisHtml(a, b, bs) {
  const compat = breedingCompatibility(a, b);
  if (!a || !b) return analysisEmptyHtml();
  if (!compat.ok) return analysisIncompatibleHtml();

  const { mode, doubleStones } = bs;
  const best = (a.mult || 1) >= (b.mult || 1) ? a : b;
  const range = coreQualityRange(a, b, { mode });
  const inherit = coreInheritChance(a, b, { doubleStones });
  const types = hooks.getSpeciesTypes(a.name) || [];
  const previewChild = { ...best, types, mult: best.mult };
  const cost = coreCost(previewChild, { mode, doubleStones });
  const stoneRows = hooks.stoneRows(cost.stones);
  const table = coreDeltaTable(mode);
  const defeats = coreIncubation({ mult: range.max });
  const maxTier = specimenTierForMult(range.max);

  const coins = hooks.getCoins();
  const pheromoneHave = hooks.getItemCount('pheromone');
  const coinsOk = coins >= cost.coins;
  const stonesOk = stoneRows.every((row) => row.owned >= row.qty);
  const pheromoneOk = mode === 'free' || pheromoneHave >= cost.pheromones;
  const incubatorFree = bs.incubator.some((slot) => !slot);
  const ready = coinsOk && stonesOk && pheromoneOk && incubatorFree;
  const ctaLabel = !incubatorFree
    ? t('breeding.incubatorFull')
    : (!ready ? t('breeding.costLacking') : t('breeding.createEgg'));

  const basePos = scalePos(range.base);
  const minPos = scalePos(range.min);
  const maxPos = scalePos(range.max);

  return `<div class="breeding-analysis">
    <div class="breeding-child">
      <span class="breeding-child-sprite"><img src="${esc(hooks.pokemonImage(a.name))}" alt=""></span>
      <div class="breeding-child-copy">
        <b>${esc(t('breeding.mysteryChild'))}</b>
        <span class="breeding-child-chips">
          <em class="chip-tier" style="--rarity:${esc(hooks.rarityColor(maxTier.name))}">${esc(rarityLabel(maxTier.name))}</em>
          ${typeBadgesHtml(types)}
          <em class="chip-lv">${esc(t('common.lv'))} 1</em>
        </span>
      </div>
    </div>

    <div class="breeding-bars">
      <div class="breeding-bar"><span>${esc(t('breeding.compat'))}</span><i class="compat"><em style="width:${compat.percent}%"></em></i><b>${compat.percent}%</b></div>
      <div class="breeding-bar"><span>${esc(t('breeding.ivInherit'))}</span><i class="inherit"><em style="width:${pct(inherit)}%"></em></i><b>${pct(inherit)}%${doubleStones ? ` <small>${esc(t('breeding.ivCopyBonus'))}</small>` : ''}</b></div>
    </div>

    <div class="breeding-quality">
      <div class="breeding-quality-head"><span>${esc(t('breeding.qualityProjected'))}</span><b>${range.min.toFixed(2)} → ${range.max.toFixed(2)}</b></div>
      <div class="breeding-quality-track">
        <i class="range" style="left:${minPos}%;width:${Math.max(1.5, maxPos - minPos)}%"></i>
        <i class="marker" style="left:${basePos}%"></i>
      </div>
      <div class="breeding-quality-scale">
        <span>${QUALITY_FLOOR.toFixed(1)}</span>
        <span>${esc(t('breeding.bestParent', { q: range.base.toFixed(2) }))}</span>
        <span>${esc(t('breeding.cap', { cap: QUALITY_CAP.toFixed(1) }))}</span>
      </div>
    </div>

    <div class="breeding-delta">
      <span class="breeding-subhead">${esc(t('breeding.deltaChances'))}</span>
      <div class="breeding-delta-pills">${table.map((row) => `
        <span class="breeding-delta-pill"><b>${esc(fmtDelta(row.value))}</b><em>${pct(row.weight)}%</em><i style="width:${pct(row.weight)}%"></i></span>`).join('')}
      </div>
    </div>

    <p class="breeding-iv-note">${esc(t('breeding.ivCopyNote'))}${doubleStones ? ` <b>${esc(t('breeding.ivCopyBonus'))}</b>` : ''}</p>

    <div class="breeding-modes" role="radiogroup" aria-label="${esc(t('breeding.activeBonuses'))}">
      <button type="button" class="breeding-mode${mode === 'pheromones' ? ' active' : ''}" data-breeding-mode="pheromones" role="radio" aria-checked="${mode === 'pheromones'}">
        ${hooks.itemImg('pheromone') ? `<img src="${esc(hooks.itemImg('pheromone'))}" alt="">` : ''}
        <span><b>${esc(t('breeding.modePheromones'))}</b><small>${esc(t('breeding.modePheromonesHint'))}</small></span>
      </button>
      <button type="button" class="breeding-mode${mode === 'free' ? ' active' : ''}" data-breeding-mode="free" role="radio" aria-checked="${mode === 'free'}">
        ${uiIcon('sparkles')}
        <span><b>${esc(t('breeding.modeFree'))}</b><small>${esc(t('breeding.modeFreeHint'))}</small></span>
      </button>
    </div>

    <div class="breeding-hatch-info">${uiIcon('egg')}<span>${esc(t('breeding.hatchInfo', { count: formatNumber(defeats) }))}</span></div>

    <div class="breeding-stones">${stoneRows.map((row) => `
      <span class="breeding-stone-chip${row.owned >= row.qty ? '' : ' lack'}">${row.img ? `<img src="${esc(row.img)}" alt="">` : ''}<b>${row.qty}× ${esc(row.name)}</b><em>${formatNumber(row.owned)}/${row.qty}</em></span>`).join('')}
    </div>

    <label class="breeding-double${doubleStones ? ' active' : ''}">
      <input type="checkbox" data-breeding-double ${doubleStones ? 'checked' : ''}>
      <span class="breeding-double-box" aria-hidden="true"></span>
      <span class="breeding-double-copy"><b>${esc(t('breeding.doubleStones'))}</b><small>${esc(t('breeding.doubleStonesHint'))}</small></span>
    </label>

    <div class="breeding-cost-row">
      <span class="breeding-cost-chip${coinsOk ? '' : ' lack'}">${idlecoinMark(14)}<b>${formatNumber(cost.coins)}</b></span>
      ${mode === 'pheromones' ? `<span class="breeding-cost-chip${pheromoneOk ? '' : ' lack'}">${hooks.itemImg('pheromone') ? `<img src="${esc(hooks.itemImg('pheromone'))}" alt="">` : ''}<b>${cost.pheromones}</b><small>(${esc(t('breeding.youHave', { count: formatNumber(pheromoneHave) }))})</small></span>` : ''}
    </div>

    <button type="button" class="breeding-cta" data-breeding-action="create" ${ready ? '' : 'disabled'}>${esc(ctaLabel)}</button>
  </div>`;
}

/* ---- incubadora + bônus ---------------------------------------------------- */
function eggSlotHtml(egg) {
  if (!egg) {
    return `<div class="breeding-egg empty">${uiIcon('egg')}<small>${esc(t('breeding.slotFree'))}</small></div>`;
  }
  const done = (egg.defeatsDone || 0) >= (egg.defeatsNeeded || 1);
  const percentDone = Math.min(100, ((egg.defeatsDone || 0) / Math.max(1, egg.defeatsNeeded || 1)) * 100);
  return `<div class="breeding-egg filled${done ? ' ready' : ''}" style="--rarity:${esc(hooks.rarityColor(egg.rarityName))}">
    <img src="${esc(hooks.pokemonImage(egg.species || egg.name))}" alt="">
    <div class="breeding-egg-copy">
      <b>${esc(hooks.title(egg.species || egg.name))}</b>
      <small>${done ? esc(t('breeding.eggReady')) : esc(t('breeding.eggProgress', { current: formatNumber(egg.defeatsDone || 0), needed: formatNumber(egg.defeatsNeeded || 0) }))}</small>
      <i><em style="width:${percentDone}%"></em></i>
    </div>
  </div>`;
}

function sideColHtml(bs) {
  const eggs = bs.incubator;
  const count = eggs.filter(Boolean).length;
  const bonusRows = [];
  if (bs.mode === 'pheromones') {
    bonusRows.push({ img: hooks.itemImg('pheromone'), title: t('breeding.modePheromones'), text: t('breeding.bonusPheromonesText') });
  } else {
    bonusRows.push({ icon: 'sparkles', title: t('breeding.modeFree'), text: t('breeding.bonusFreeText') });
  }
  if (bs.doubleStones) {
    bonusRows.push({ icon: 'diamond', title: t('breeding.bonusDouble'), text: t('breeding.bonusDoubleText') });
  }
  return `<aside class="breeding-side-col">
    <section class="breeding-panel breeding-incubator">
      <h3>${esc(t('breeding.incubator'))}<span class="breeding-inc-count">${count}/2</span></h3>
      <div class="breeding-egg-slots">${eggs.map(eggSlotHtml).join('')}</div>
      ${count === 0 ? `<p class="breeding-inc-empty">${esc(t('breeding.noEggs'))}</p>` : ''}
    </section>
    <section class="breeding-panel breeding-bonus-panel">
      <h3>${esc(t('breeding.activeBonuses'))}</h3>
      <div class="breeding-bonus-rows">${bonusRows.map((row) => `
        <div class="breeding-bonus-row">
          <span class="breeding-bonus-icon">${row.img ? `<img src="${esc(row.img)}" alt="">` : uiIcon(row.icon || 'sparkles')}</span>
          <span><b>${esc(row.title)}</b><small>${esc(row.text)}</small></span>
        </div>`).join('')}
      </div>
    </section>
  </aside>`;
}

/* ---- box ------------------------------------------------------------------- */
const SORTERS = {
  iv: (x, y) => hooks.ivScore(y) - hooks.ivScore(x),
  quality: (x, y) => (Number(y.mult) || 1) - (Number(x.mult) || 1),
  level: (x, y) => (Number(y.level) || 1) - (Number(x.level) || 1),
  name: (x, y) => hooks.title(x.name).localeCompare(hooks.title(y.name)),
};

function boxHtml(bs) {
  const q = ui.query.trim().toLowerCase();
  const paired = new Set(bs.pair.filter(Boolean).map(String));
  const [pairA, pairB] = pairPokemon();
  const single = pairA && !pairB ? pairA : pairB && !pairA ? pairB : null;
  let list = hooks.getBoxPokemon().filter((p) => {
    if (paired.has(String(p.id))) return false;
    if (!q) return true;
    return String(p.name).includes(q) || hooks.title(p.name).toLowerCase().includes(q);
  });
  list.sort(SORTERS[ui.sort] || SORTERS.iv);
  list = list.slice(0, 120);
  const sortOptions = [
    ['iv', t('breeding.sortIv')],
    ['quality', t('breeding.sortQuality')],
    ['level', t('breeding.sortLevel')],
    ['name', t('breeding.sortName')],
  ];
  return `<section class="breeding-panel breeding-box">
    <header>
      <h3>${esc(t('breeding.box'))}</h3>
      <div class="breeding-box-tools">
        <label class="breeding-box-search"><input id="breedingSearch" value="${esc(ui.query)}" placeholder="${esc(t('breeding.searchPlaceholder'))}"></label>
        <select id="breedingSort" aria-label="${esc(t('breeding.sortIv'))}">${sortOptions.map(([value, label]) => `<option value="${value}"${ui.sort === value ? ' selected' : ''}>${esc(label)}</option>`).join('')}</select>
      </div>
    </header>
    <div class="breeding-box-grid">
      ${list.map((p) => {
        const compatible = single && String(p.name) === String(single.name);
        return `<button type="button" class="breeding-box-tile${compatible ? ' compatible' : ''}" data-breeding-box="${esc(p.id)}" style="--rarity:${esc(hooks.rarityColor(p.rarity))}">
          <img src="${esc(hooks.pokemonImage(p.name))}" alt="" loading="lazy">
          <b>${esc(hooks.title(p.name))}</b>
          <span class="breeding-box-chips"><em>IV ${esc(hooks.ivScore(p))}</em><em>Q ${Number(p.mult || 1).toFixed(2)}</em></span>
        </button>`;
      }).join('') || `<div class="breeding-empty">${esc(t('breeding.emptyBox'))}</div>`}
    </div>
  </section>`;
}

function render() {
  if (!root) return;
  const focused = document.activeElement?.id === 'breedingSearch';
  const caret = focused ? document.activeElement.selectionStart : null;
  const bs = breedingState();
  const [a, b] = pairPokemon();
  root.innerHTML = `<div class="breeding-overlay-card" role="dialog" aria-modal="true" aria-labelledby="breedingTitle">
    ${headerHtml()}
    <div class="breeding-layout">
      ${pairColHtml(a, b)}
      <section class="breeding-panel breeding-center-col"><h3>${esc(t('breeding.analysis'))}</h3>${analysisHtml(a, b, bs)}</section>
      ${sideColHtml(bs)}
    </div>
    ${boxHtml(bs)}
  </div>`;
  if (focused) {
    const input = root.querySelector('#breedingSearch');
    if (input) { input.focus(); const position = Math.min(caret ?? input.value.length, input.value.length); input.setSelectionRange(position, position); }
  }
}

function bind() {
  root.addEventListener('click', (event) => {
    if (event.target === root) { closeBreeding(); return; }
    if (event.target.closest('[data-breeding-action="close"]')) { closeBreeding(); return; }
    const clear = event.target.closest('[data-breeding-clear]');
    if (clear) {
      ui.pickingSlot = null;
      hooks.onClearParent(Number(clear.dataset.breedingClear));
      render();
      return;
    }
    const modeButton = event.target.closest('[data-breeding-mode]');
    if (modeButton) { hooks.onSetMode(modeButton.dataset.breedingMode); render(); return; }
    const pick = event.target.closest('[data-breeding-pick]');
    if (pick) {
      const slot = Number(pick.dataset.breedingPick);
      ui.pickingSlot = ui.pickingSlot === slot ? null : slot;
      render();
      return;
    }
    const box = event.target.closest('[data-breeding-box]');
    if (box) {
      const pair = breedingState().pair;
      const slot = ui.pickingSlot ?? (pair[0] == null ? 0 : pair[1] == null ? 1 : 0);
      hooks.onSelectParent(slot, box.dataset.breedingBox);
      ui.pickingSlot = null;
      render();
      return;
    }
    if (event.target.closest('[data-breeding-action="create"]')) {
      const result = hooks.onCreateEgg({
        breedChild, breedingCompatibility, breedingCost, incubationDefeatsFor, rollBreedingDelta,
        pheromoneCostFor, normalizeBreedingMode,
      });
      Promise.resolve(result).then((outcome) => {
        if (outcome?.ok) hooks.showToast(t('breeding.eggCreated'));
        else if (outcome?.reason === 'coins') hooks.showToast(t('breeding.notEnoughCoins'));
        else if (outcome?.reason === 'stones') hooks.showToast(t('breeding.notEnoughStones'));
        else if (outcome?.reason === 'pheromones') hooks.showToast(t('breeding.notEnoughPheromones'));
        else if (outcome?.reason === 'incubator') hooks.showToast(t('breeding.incubatorFullToast'));
        else if (outcome?.reason === 'pair') hooks.showToast(t('breeding.needPairToast'));
        else if (outcome?.reason === 'incompatible') hooks.showToast(t('breeding.incompatible'));
        render();
      });
    }
  });
  root.addEventListener('change', (event) => {
    if (event.target.matches('[data-breeding-double]')) {
      hooks.onToggleDoubleStones(event.target.checked);
      render();
      return;
    }
    if (event.target.id === 'breedingSort') {
      ui.sort = event.target.value;
      render();
    }
  });
  root.addEventListener('input', (event) => {
    if (event.target.id === 'breedingSearch') { ui.query = event.target.value; render(); }
  });
}

export function initBreedingUI(options = {}) {
  hooks = { ...hooks, ...options };
  root = document.createElement('div');
  root.id = 'breedingOverlay';
  root.className = 'breeding-overlay hidden';
  document.body.append(root);
  bind();
  onLocaleChange(() => { if (ui.open) render(); });
  addEventListener('keydown', (event) => { if (event.key === 'Escape' && ui.open) closeBreeding(); });
}

export function openBreeding() {
  if (!root || ui.open) return;
  ui.open = true;
  root.classList.remove('hidden');
  document.body.classList.add('breeding-open');
  render();
}

export function closeBreeding() {
  if (!ui.open) return;
  ui.open = false;
  root.classList.add('hidden');
  document.body.classList.remove('breeding-open');
}

export function refreshBreeding() { if (ui.open) render(); }
export function isBreedingOpen() { return ui.open; }
